import heic2any from "heic2any";

const type = "image/jpeg";
const maxWidth = 1920;

/**
 * Validate Image File
 * @param {File} file - upload file
 */
const isInValidFile = file => !file || file.size === 0;

/**
 * Create a new File object from a Blob
 * @param {File} file
 * @param {Blob} blob - The blob data
 * @returns {File} The created File object
 */
const createNewFile = (name, blob) => new File([blob], name, { type });

/**
 * Resize Image File
 * @param {File} file - upload file
 * @returns {Promise<File>} Resized file
 */
export const resize = async file => {
  try {
    if (isInValidFile(file)) throw new Error("Invalid file");

    const loader = new Promise(resolve => {
      // Initialize
      const reader = new FileReader();
      const image = new Image();

      // Onload Image
      image.onload = () => {
        // Canvas Element
        const canvas = document.createElement("canvas");

        // Original Width & Height
        const { width, height } = image;

        // Default Image Size
        const size = { width, height };

        // Validate Size
        if (maxWidth && width > maxWidth) {
          // Calculate Scale Height
          const scale = (maxWidth * 100) / width;

          // Calculate Height
          size.width = maxWidth;
          size.height = (scale / 100) * height;
        }

        // Set Canvas Size
        canvas.width = size.width;
        canvas.height = size.height;

        // Draw Image
        canvas.getContext("2d").drawImage(image, 0, 0, size.width, size.height);

        // Image Blob
        canvas.toBlob(blob => {
          resolve(blob);
        }, file.type);
      };

      // Load File Handler
      reader.onload = event => {
        image.src = event.target.result;
      };

      // Read File
      reader.readAsDataURL(file);
    });

    // Image Result
    const blob = await loader.then();

    // Create Image File
    const newFile = createNewFile(file.name, blob);
    if (isInValidFile(newFile)) throw new Error("Error creating file");

    return newFile;
  } catch (error) {
    return null;
  }
};

/**
 * Convert HEIC or HEIF to JPEG
 * @param {File} file - HEIC or HEIF file
 * @returns {Promise<File>} Converted JPEG file
 */
export const resizeHeicOrHeif = async file => {
  try {
    if (isInValidFile(file)) throw new Error("Invalid file");

    const hasFileSizeExceededLimit = file.size / 1024 / 1024 > 7;
    const quality = hasFileSizeExceededLimit ? 0.3 : 0.5;
    const convertedBlob = await heic2any({
      blob: file,
      toType: type,
      quality
    });

    // Create Image File
    const convertedFile = createNewFile(file.name, convertedBlob);
    if (isInValidFile(convertedFile))
      throw new Error("Error converting HEIC/HEIF to JPEG");

    const resizedFile = await resize(convertedFile);
    if (isInValidFile(convertedFile))
      throw new Error("Error resizing HEIC/HEIF");

    return resizedFile;
  } catch (error) {
    return null;
  }
};
